import React, { useEffect, useState } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { TYPE_LABELS } from "./FeriePage";
import { euro } from "./TrasfertePage";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MonthlyGridTab } from "./report/MonthlyGridTab";
import { SpecialLeavesTab } from "./report/SpecialLeavesTab";
import { TourAcksTab } from "./report/TourAcksTab";
import { ContractTermsTab } from "./report/ContractTermsTab";
import {
  BarChart3, Users, CalendarDays, Clock, UserCheck, Hourglass, Plane, Building2, CalendarRange, GraduationCap, BookOpenCheck, FileClock,
} from "lucide-react";

const KPI = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 flex items-center gap-4">
    <span className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1a`, color }}><Icon size={24} /></span>
    <div className="min-w-0">
      <div className="text-[24px] font-black text-[#16345c] leading-none truncate">{value}</div>
      <div className="text-[12px] text-gray-500 mt-1">{label}</div>
    </div>
  </div>
);

const BarChartCard = ({ title, icon: Icon, data, unit, color }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <h3 className="text-[15px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Icon size={17} /> {title}</h3>
      {data.length === 0 ? (
        <div className="text-[13px] text-gray-400 py-6 text-center">Nessun dato</div>
      ) : (
        <div className="space-y-3">
          {data.map((d) => (
            <div key={d.label}>
              <div className="flex items-center justify-between text-[12.5px] mb-1">
                <span className="text-gray-600 font-medium">{d.label}</span>
                <span className="text-[#16345c] font-bold">{d.value}{unit ? ` ${unit}` : ""}</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(d.value / max) * 100}%`, backgroundColor: color }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TYPE_COLORS = { ferie: "#16345c", permesso: "#e67e22", malattia: "#c0392b", permesso_104: "#8e44ad", lutto: "#607d8b" };

const ReportPage = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const depts = data?.departments || [];
  const ferieData = depts.map((d) => ({ label: d.department, value: d.ferie_days }));
  const permessoData = depts.map((d) => ({ label: d.department, value: d.permesso_hours }));
  const presenceData = depts.map((d) => ({ label: `${d.department}`, value: d.present_today }));
  const typeData = Object.entries(data?.type_distribution || {}).map(([k, v]) => ({ label: TYPE_LABELS[k] || k, value: v, color: TYPE_COLORS[k] || "#16345c" }));
  const typeMax = Math.max(1, ...typeData.map((d) => d.value));

  const dashboard = !data ? (
    <div className="py-16 text-center text-gray-400">Caricamento report...</div>
  ) : (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI icon={Users} label="Dipendenti totali" value={data.kpi.employees} color="#16345c" />
        <KPI icon={CalendarDays} label="Giorni ferie approvati" value={data.kpi.ferie_days_total} color="#009640" />
        <KPI icon={Clock} label="Ore permessi approvate" value={data.kpi.permesso_hours_total} color="#e67e22" />
        <KPI icon={UserCheck} label="Presenti oggi" value={data.kpi.present_today} color="#0d6cb8" />
        <KPI icon={Hourglass} label="Richieste in attesa" value={data.kpi.pending_leaves} color="#c0392b" />
        <KPI icon={Plane} label="Rimborsi trasferte approvati" value={euro(data.kpi.trips_amount_approved)} color="#8e44ad" />
        <KPI icon={Building2} label="Reparti" value={data.kpi.departments} color="#5c6bc0" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarChartCard title="Giorni di ferie per reparto" icon={CalendarDays} data={ferieData} unit="gg" color="#16345c" />
        <BarChartCard title="Ore di permesso per reparto" icon={Clock} data={permessoData} unit="h" color="#e67e22" />
        <BarChartCard title="Presenti oggi per reparto" icon={UserCheck} data={presenceData} unit="" color="#0d6cb8" />
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-[15px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><BarChart3 size={17} /> Distribuzione richieste per tipo</h3>
          {typeData.length === 0 ? (
            <div className="text-[13px] text-gray-400 py-6 text-center">Nessun dato</div>
          ) : (
            <div className="space-y-3">
              {typeData.map((d) => (
                <div key={d.label}>
                  <div className="flex items-center justify-between text-[12.5px] mb-1">
                    <span className="text-gray-600 font-medium">{d.label}</span>
                    <span className="text-[#16345c] font-bold">{d.value}</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(d.value / typeMax) * 100}%`, backgroundColor: d.color }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><BarChart3 size={24} /> Report</h1>
        <p className="text-[14px] text-gray-500 mb-6">Cruscotto HR, prospetto presenze per il consulente del lavoro e storico istituti particolari</p>

        <Tabs defaultValue="dashboard">
          <TabsList className="mb-6 h-auto w-full md:w-auto max-w-full justify-start flex-nowrap overflow-x-auto no-scrollbar">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="flex items-center gap-2"><BarChart3 size={15} /> Cruscotto</TabsTrigger>
            <TabsTrigger value="griglia" data-testid="tab-griglia" className="flex items-center gap-2"><CalendarRange size={15} /> Griglia presenze</TabsTrigger>
            <TabsTrigger value="special" data-testid="tab-special" className="flex items-center gap-2"><GraduationCap size={15} /> Aspettative e studio</TabsTrigger>
            <TabsTrigger value="tour" data-testid="tab-tour" className="flex items-center gap-2"><BookOpenCheck size={15} /> Presa visione tour</TabsTrigger>
            <TabsTrigger value="contract-terms" data-testid="tab-contract-terms" className="flex items-center gap-2"><FileClock size={15} /> Preavviso e prova</TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard">{dashboard}</TabsContent>
          <TabsContent value="griglia"><MonthlyGridTab /></TabsContent>
          <TabsContent value="special"><SpecialLeavesTab /></TabsContent>
          <TabsContent value="tour"><TourAcksTab /></TabsContent>
          <TabsContent value="contract-terms"><ContractTermsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ReportPage;
